<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import SidenavItem from "./SidenavItem.vue";
import Loading from "@/components/Loading";
import { changeEnvironment } from "@/api/auth";
import Swal from "sweetalert2";
import { isAkunDemo } from "@/utils/role-utils";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Get role from Vuex store (secure - fetched from API)
const role = computed(() => store.getters['auth/userRole']);

const isLoading = ref(false);
const environment = computed(() => store.getters.currentEnvironment);
const isSandbox = computed(() => environment.value !== "production");
const isDemoAccount = computed(() => isAkunDemo());

function toggleEnvironment() {
  setEnvironment(isSandbox.value ? "production" : "sandbox");
}

async function setEnvironment(env) {
  // Validate KYB status before allowing production environment switch
  if (env === 'production') {
    isLoading.value = true;
    let profileFetched = false;
    try {
      // Fetch fresh profile from API to get current KYB status (secure - cannot be manipulated)
      const result = await store.dispatch('auth/fetchUserProfile');
      profileFetched = result.success;
    } catch (error) {
      console.error("Error fetching profile for KYB validation:", error);
    } finally {
      isLoading.value = false;
    }

    if (!profileFetched) {
      await Swal.fire({
        title: 'Error',
        text: 'Unable to verify your KYB status. Please try again later.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    if (store.getters['auth/statusKyb'] !== 'approved') {
      await Swal.fire({
        title: 'Access Denied',
        text: 'You cannot use the Production environment yet. Please submit your company data first and wait for your KYB status to be approved.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6'
      });
      return; // Stop execution if KYB is not approved
    }
  }

  try {
    const formData = new FormData();
    formData.append("environment", env);

    const response = await changeEnvironment(formData);

    if (response.status === "success") {
      // Update store after successful API call
      store.dispatch("changeEnvironment", env);

      // Set app_id and app_secret from API response
      if (response.data && response.data.app_id && response.data.app_secret) {
        localStorage.setItem("merchantAppId", response.data.app_id);
        localStorage.setItem("merchantAppSecret", response.data.app_secret);

        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('environmentChanged', {
          detail: {
            environment: env,
            appId: response.data.app_id,
            appSecret: response.data.app_secret
          }
        }));

        // refresh page
        window.location.reload();
      } else {
        console.warn("No app_id or app_secret in response data:", response.data);
      }
    } else {
      console.error("Failed to change environment:", response.message);
    }
  } catch (error) {
    console.error("Error changing environment:", error);
  }
}

const showTransferMenu = ref(false);

function toggleTransferMenu() {
  showTransferMenu.value = !showTransferMenu.value;
}

function navigateTo(path) {
  showTransferMenu.value = false;
  router.push(path);
}

function handleClickOutside(event) {
  const wrapper = document.querySelector('.transfer-wrapper');
  if (wrapper && !wrapper.contains(event.target)) {
    showTransferMenu.value = false;
  }
}

// Check if PPOB is enabled for this merchant
const isPpobEnabled = computed(() => store.getters['auth/isPpobEnabled']);

// Check if user is BI user
const isBiUser = computed(() => store.getters['auth/isBiUser']);

// Fetch user profile on mount to ensure role is loaded
onMounted(async () => {
  document.addEventListener('click', handleClickOutside);
  if (!store.getters['auth/userRole']) {
    await store.dispatch('auth/fetchUserProfile');
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Check if user has role to see transfer button (Finance or Admin)
const hasTransferRole = computed(() => ['Finance', 'Admin'].includes(role.value));

// menu master list
const menuItems = ref([
  {
    name: "Home",
    to: "/home",
    key: "home",
    icon: "/assets/icons/ic_home.png",
    allowedRoles: ["Admin", "IT", "Finance", "Staff"],
  },
  {
    name: "Balance",
    to: "/balance",
    key: "balance",
    icon: "/assets/icons/ic_wallet.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Topup",
    to: "/upload-ppob",
    key: "upload-ppob",
    icon: "/assets/icons/ic_wallet.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Transaction",
    to: "/transaction",
    key: "transaction",
    icon: "/assets/icons/ic_report.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Integrations",
    to: "/integrations",
    key: "integrations",
    icon: "/assets/icons/ic_integration.svg",
    allowedRoles: ["Admin", "IT"],
  },
  {
    name: "User Management",
    to: "/user-management",
    key: "user-management",
    icon: "/assets/icons/ic_user_management.svg",
    activeIcon: "/assets/icons/ic_user_management_active.svg",
    allowedRoles: ["Admin"],
  },
  {
    name: "Help",
    to: "/help-center",
    key: "help-center",
    icon: "/assets/icons/ic_help.png",
    allowedRoles: ["Admin", "IT", "Finance", "Staff"],
  },
]);

// filter berdasarkan role (using computed role from store)
// dan hide Balance & Integrations menu jika company_name === 'AKUN DEMO'
const filteredMenu = computed(() => {
  const isDemoAccount = isAkunDemo();
  return menuItems.value.filter((item) => {
    // Hide Balance and Integrations menu if company_name === 'AKUN DEMO'
    if ((item.key === 'balance' || item.key === 'integrations' || item.key === 'user-management') && isDemoAccount) {
      return false;
    }
    
    // Hide Balance menu if user is BI user
    if ((item.key === 'balance' || item.key === 'integrations' || item.key === 'user-management' || item.key === 'upload-ppob' || item.key === 'help-center') && isBiUser.value) {
      return false;
    }
    
    // Hide Topup (PPOB) menu if is_ppob is not enabled
    if (item.key === 'upload-ppob' && !isPpobEnabled.value) {
      return false;
    }
    
    return item.allowedRoles.includes(role.value);
  });
});

const getCurrentRoute = computed(() => {
  return route.path.split("/")[1];
});
</script>

<template>
  <aside class="sidebar fixed-start" id="sidenav-main">
    <div class="sidebar-body">
      <ul class="nav-list">
        <li v-if="!isDemoAccount" class="px-4 pt-2 environment-wrapper">
          <button
            type="button"
            role="switch"
            :aria-checked="isSandbox"
            class="environment-switch"
            :class="isSandbox ? 'is-sandbox' : 'is-production'"
            @click="toggleEnvironment"
          >
            <span class="switch-track">
              <span class="switch-thumb"></span>
            </span>
            <span class="switch-label">
              {{ isSandbox ? "Sandbox Mode" : "Production Mode" }}
            </span>
          </button>
        </li>

        <!-- Transfer button - show if user has Finance/Admin role -->
        <!-- Router guard will redirect to Forbidden if KYB not approved -->
        <li
          class="p-4 transfer-wrapper"
          v-if="hasTransferRole"
        >
          <button
            class="w-100 transfer-button transfer-text"
            @click.stop="toggleTransferMenu"
          >
            Transfer
            <img
              src="/assets/ic_send.png"
              alt="Send Icon"
              style="width: 24px; height: 24px; margin-left: 8px"
            />
          </button>

          <!-- Floating transfer type menu -->
          <div v-if="showTransferMenu" class="transfer-menu" @click.stop>
            <button class="transfer-menu-item" @click="navigateTo('/transfer')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="5" width="13" height="16" rx="2" stroke="#2D6A57" stroke-width="1.5" fill="none"/>
                <rect x="6" y="3" width="13" height="16" rx="2" stroke="#2D6A57" stroke-width="1.5" fill="white"/>
                <line x1="9" y1="8" x2="16" y2="8" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="9" y1="11" x2="16" y2="11" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="9" y1="14" x2="13" y2="14" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <span class="transfer-menu-label">Bulk Transfer</span>
            </button>
            <div class="transfer-menu-divider"></div>
            <button class="transfer-menu-item" @click="navigateTo('/single-transfer')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="3" width="14" height="18" rx="2" stroke="#2D6A57" stroke-width="1.5" fill="none"/>
                <line x1="8" y1="8" x2="16" y2="8" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="8" y1="11" x2="16" y2="11" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="8" y1="14" x2="12" y2="14" stroke="#2D6A57" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <span class="transfer-menu-label">Single Transfer</span>
            </button>
          </div>
        </li>

        <hr />

        <!-- Loop menu items -->
        <li
          v-for="item in filteredMenu"
          :key="item.key"
          :class="{ active: getCurrentRoute === item.key }"
        >
          <sidenav-item :to="item.to" :icon="item.key">
            <template v-slot:icon>
              <img
                :src="
                  item.key === 'user-management' &&
                  getCurrentRoute === 'user-management'
                    ? item.activeIcon
                    : item.icon
                "
                alt=""
                width="24"
                height="24"
              />
            </template>
            {{ item.name }}
          </sidenav-item>
        </li>
      </ul>
    </div>
    <Loading :isLoading="isLoading" />
  </aside>
</template>

<style scoped>
.sidebar {
  width: 250px;
  height: calc(100vh - 56px - var(--sandbox-banner-height, 0px));
  margin-top: 56px;
  background-color: #fff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  /* position: fixed; */
  top: 56px;
  /* left: 0; */
  /* z-index: 999; */
}

.sidebar-header {
  padding: 20px 16px;
  border-bottom: 1px solid #e9ecef;
}

.logo-container {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo {
  margin-right: 10px;
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
  color: #4caf50;
}

.sidebar-body {
  flex: 1;
  padding: 16px 0;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 10px;
}

.nav-list li {
  margin-bottom: 4px;
}

.nav-list li.active {
  background-color: #e9fbf0;
  border-radius: 100px;
  color: #128050;
  font-weight: 600;
  text-decoration: none;
}

.transfer-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  border-radius: 100px;
  border: none;
  padding: 8px 16px 8px 24px;
  background-color: #43bb75;
}

.transfer-text {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  line-height: 150%;
  letter-spacing: 0%;
  vertical-align: middle;
}

.transfer-wrapper {
  position: relative;
}

.environment-switch {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
}

.switch-track {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 24px;
  border-radius: 100px;
  transition: background-color 0.2s ease;
}

.switch-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.switch-thumb::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border: 1.5px solid currentColor;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.is-sandbox .switch-track {
  background-color: #e0712c;
}

.is-sandbox .switch-thumb {
  color: #e0712c;
  transform: translateX(20px);
}

.is-production .switch-track {
  background-color: #43bb75;
}

.is-production .switch-thumb {
  color: #43bb75;
}

.switch-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #1f2937;
  white-space: nowrap;
}

.transfer-menu {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: calc(100% + 8px);
  width: 220px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  overflow: hidden;
}

.transfer-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.transfer-menu-item:hover {
  background-color: #f0faf5;
}

.transfer-menu-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: #2d6a57;
  line-height: 1.4;
}

.transfer-menu-divider {
  height: 1px;
  background-color: #e9ecef;
  margin: 0 20px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.badge-maintenance {
  position: absolute;
  top: 8px;
  right: 12px;
  background-color: orange;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: bold;
  z-index: 10;
  box-shadow: 0 0 4px rgba(220, 53, 69, 0.6);
  animation: pulse 1.5s infinite ease-in-out;
}
</style>
